import { NAVY, GOLD, WHITE } from "./palette";

// ════════════════════════════════════════════════════════════════════
//  apple-native — a calm, Apple-quality skin for iScoopCRM.
//  Same class names as every theme, so components are untouched. The look
//  comes from: off-white canvas, white floating surfaces with soft large-blur
//  shadows, ONE blue accent used sparingly, near-black text, generous spacing,
//  refined corners, frosted modals/topbar, and tactile hover/press.
//  Tokens live in :root so you can tweak the whole language in one place.
// ════════════════════════════════════════════════════════════════════
export const globalCss = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  :root{
    --bg:#F5F5F7;            /* Apple off-white canvas */
    --surface:#FFFFFF;       /* floating cards */
    --text:#1D1D1F;          /* near-black */
    --text2:#86868B;         /* secondary */
    --text3:#A1A1A6;         /* tertiary / placeholder */
    --border:rgba(0,0,0,.08);/* hairline dividers */
    --accent:${GOLD};        /* the one accent (blue) */
    --accent-weak:rgba(0,113,227,.10);
    --accent-hover:#0077ED;
    --danger:#D7322B;
    --ok:#1A8A52;
    /* refined corners */
    --r-sm:8px; --r:12px; --r-lg:16px; --r-xl:20px;
    /* soft, low-opacity, large-blur shadows — things float just above the bg */
    --shadow-sm:0 1px 2px rgba(0,0,0,.04), 0 1px 1px rgba(0,0,0,.02);
    --shadow:0 1px 3px rgba(0,0,0,.04), 0 8px 24px rgba(0,0,0,.05);
    --shadow-lg:0 12px 48px rgba(0,0,0,.14);
    --ease:cubic-bezier(.4,0,.2,1);
  }

  *{box-sizing:border-box;margin:0;padding:0}
  /* SF on Apple devices, Inter everywhere else */
  body{font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',system-ui,sans-serif;
       background:var(--bg);color:var(--text);font-size:14px;-webkit-font-smoothing:antialiased;letter-spacing:-.01em}
  ::-webkit-scrollbar{width:8px;height:8px}::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:rgba(0,0,0,.16);border-radius:8px;border:2px solid transparent;background-clip:content-box}
  ::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.28);background-clip:content-box}

  /* ── App shell ── */
  .app{display:flex;height:100vh;overflow:hidden}

  /* ── Sidebar (dark, calm, monochrome with one accent) ── */
  .sidebar{width:240px;min-width:240px;background:${NAVY};display:flex;flex-direction:column;position:sticky;top:0;height:100vh;overflow-y:auto}
  .sidebar-logo{padding:22px 20px 18px;border-bottom:1px solid rgba(255,255,255,.07)}
  .sidebar-logo h1{font-size:16px;font-weight:600;color:${WHITE};line-height:1.3;letter-spacing:-.02em}
  .sidebar-logo span{color:var(--accent)}
  .sidebar-logo p{color:rgba(255,255,255,.45);font-size:11px;margin-top:3px;font-weight:400}
  .nav{padding:12px 12px;flex:1}
  .nav-section{font-size:11px;font-weight:600;letter-spacing:.04em;color:rgba(255,255,255,.32);padding:16px 10px 6px;text-transform:uppercase}
  .nav-item{display:flex;align-items:center;gap:11px;padding:9px 12px;border-radius:var(--r-sm);cursor:pointer;font-size:13.5px;font-weight:500;color:rgba(255,255,255,.62);transition:.18s var(--ease);margin-bottom:2px;line-height:1}
  .nav-item:hover{background:rgba(255,255,255,.08);color:rgba(255,255,255,.95)}
  .nav-item.active{background:var(--accent);color:#fff;font-weight:600}          /* active = solid accent, Apple-sidebar style */
  .nav-item svg{width:17px;height:17px;flex-shrink:0;opacity:.85}
  .nav-item.active svg{opacity:1}
  .sidebar-footer{padding:16px;border-top:1px solid rgba(255,255,255,.07);font-size:11px;color:rgba(255,255,255,.3)}

  /* ── Main area ── */
  .main{flex:1;overflow-y:auto;overflow-x:auto;background:var(--bg);min-width:0;min-height:0}

  /* ── Topbar (frosted / vibrancy) ── */
  .topbar{background:rgba(255,255,255,.72);backdrop-filter:saturate(180%) blur(20px);-webkit-backdrop-filter:saturate(180%) blur(20px);
          border-bottom:1px solid var(--border);padding:0 32px;height:60px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:10}
  .topbar h2{font-size:19px;font-weight:600;color:var(--text);letter-spacing:-.02em}
  .topbar-actions{display:flex;gap:10px;align-items:center}
  .topbar-date{font-size:12.5px;color:var(--text2);padding:0 4px}

  /* ── Page content (generous) ── */
  .content{padding:28px 32px}

  /* ── Cards (white, floating, soft shadow, rounded) ── */
  .card{background:var(--surface);border-radius:var(--r-lg);border:1px solid var(--border);padding:22px;box-shadow:var(--shadow);transition:box-shadow .2s var(--ease),transform .2s var(--ease)}
  .card-title{font-size:14px;font-weight:600;color:var(--text);margin-bottom:16px;letter-spacing:-.01em}

  /* ── Stat cards ── */
  .stats-row{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:22px}
  .stat-card{background:var(--surface);border-radius:var(--r-lg);border:1px solid var(--border);padding:20px 22px;box-shadow:var(--shadow);transition:transform .2s var(--ease),box-shadow .2s var(--ease)}
  .stat-card:hover{transform:translateY(-2px);box-shadow:var(--shadow-lg)}     /* tactile lift */
  .stat-label{font-size:11px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:var(--text2);margin-bottom:10px}
  .stat-value{font-size:30px;font-weight:600;color:var(--text);letter-spacing:-.03em;line-height:1}
  .stat-sub{font-size:12px;color:var(--text2);margin-top:6px}
  .stat-accent{width:34px;height:3px;border-radius:2px;background:var(--accent);margin-bottom:12px}

  /* ── Buttons (weight + press feel) ── */
  .btn{padding:9px 17px;border-radius:var(--r-sm);border:none;cursor:pointer;font-family:inherit;font-size:13px;font-weight:500;transition:.18s var(--ease);display:inline-flex;align-items:center;gap:6px;line-height:1;white-space:nowrap}
  .btn:disabled{opacity:.45;cursor:not-allowed}
  .btn:active:not(:disabled){transform:scale(.97)}
  .btn-primary{background:var(--accent);color:#fff;box-shadow:0 1px 2px rgba(0,113,227,.3)}
  .btn-primary:hover:not(:disabled){background:var(--accent-hover);box-shadow:0 2px 8px rgba(0,113,227,.32)}
  .btn-gold{background:var(--accent);color:#fff;font-weight:600;box-shadow:0 1px 2px rgba(0,113,227,.3)}   /* "gold" CTAs become the accent */
  .btn-gold:hover:not(:disabled){background:var(--accent-hover);box-shadow:0 2px 8px rgba(0,113,227,.32)}
  .btn-ghost{background:var(--surface);color:var(--text);border:1px solid var(--border)}
  .btn-ghost:hover:not(:disabled){background:#fbfbfd;border-color:rgba(0,0,0,.16)}
  .btn-danger{background:rgba(215,50,43,.1);color:var(--danger)}
  .btn-danger:hover{background:rgba(215,50,43,.16)}
  .btn-sm{padding:6px 13px;font-size:12px}
  .btn-xs{padding:4px 10px;font-size:11px;border-radius:6px}

  /* ── Table (quiet, spacious) ── */
  .table-wrap{overflow-x:auto}
  table{width:100%;border-collapse:collapse}
  thead tr{border-bottom:1px solid var(--border)}
  th{font-size:11px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;color:var(--text2);padding:11px 16px;text-align:left;white-space:nowrap}
  td{padding:14px 16px;font-size:13.5px;border-bottom:1px solid var(--border);color:var(--text)}
  tr:last-child td{border-bottom:none}
  tbody tr{transition:background .15s var(--ease)}
  tr:hover td{background:#fafafd}

  /* ── Badges (subtle pills) ── */
  .badge{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:500;letter-spacing:0}
  .badge-green{background:rgba(26,138,82,.12);color:var(--ok)}
  .badge-gold{background:var(--accent-weak);color:var(--accent)}
  .badge-blue{background:var(--accent-weak);color:var(--accent)}
  .badge-gray{background:rgba(0,0,0,.05);color:var(--text2)}
  .badge-red{background:rgba(215,50,43,.1);color:var(--danger)}
  .badge-purple{background:rgba(122,86,231,.12);color:#6E56CF}

  /* ── Forms (near-borderless, accent ring on focus) ── */
  .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
  .form-group{display:flex;flex-direction:column;gap:6px}
  .form-group.full{grid-column:1/-1}
  label{font-size:12px;font-weight:500;color:var(--text2)}
  input,select,textarea{padding:10px 13px;border:1px solid var(--border);border-radius:10px;font-family:inherit;font-size:13.5px;color:var(--text);outline:none;transition:.16s var(--ease);background:var(--surface)}
  input:focus,select:focus,textarea:focus{border-color:var(--accent);box-shadow:0 0 0 4px var(--accent-weak)}
  input::placeholder{color:var(--text3)}
  textarea{resize:vertical;min-height:80px}
  select{cursor:pointer}

  /* ── Modal (frosted backdrop, spring slide-up) ── */
  @keyframes sheetIn{from{opacity:0;transform:translateY(12px) scale(.985)}to{opacity:1;transform:none}}
  .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.32);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);z-index:100;display:flex;align-items:center;justify-content:center;padding:24px}
  .modal{background:var(--surface);border-radius:var(--r-xl);width:100%;max-width:560px;max-height:92vh;overflow-y:auto;box-shadow:var(--shadow-lg);animation:sheetIn .26s var(--ease)}
  .modal-header{padding:22px 26px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;background:var(--surface);z-index:1}
  .modal-header h3{font-size:18px;font-weight:600;color:var(--text);letter-spacing:-.02em}
  .modal-body{padding:24px 26px}
  .modal-footer{padding:18px 26px;border-top:1px solid var(--border);display:flex;justify-content:flex-end;gap:10px;position:sticky;bottom:0;background:var(--surface)}
  .close-btn{background:none;border:none;cursor:pointer;color:var(--text3);font-size:22px;line-height:1;padding:2px;transition:.15s var(--ease)}
  .close-btn:hover{color:var(--text)}

  /* ── Week grid ── */
  .week-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:10px}
  .day-col{background:var(--surface);border-radius:var(--r);border:1px solid var(--border);overflow:hidden;box-shadow:var(--shadow-sm)}
  .day-header{background:#fafafd;color:var(--text2);font-size:11px;font-weight:600;text-align:center;padding:9px 4px;text-transform:uppercase;letter-spacing:.05em}
  .day-header.today{background:var(--accent);color:#fff}
  .day-jobs{padding:7px}
  .job-chip{background:#f5f5f7;border-left:3px solid var(--accent);border-radius:8px;padding:6px 8px;margin-bottom:5px;font-size:11px;cursor:pointer;transition:.15s var(--ease)}
  .job-chip:hover{background:#ececf0;transform:translateX(1px)}
  .job-chip.done{background:rgba(26,138,82,.1);border-color:var(--ok);opacity:.75}
  .job-chip .client-name{font-weight:600;color:var(--text);display:block;font-size:11px}
  .job-chip.done .client-name{color:var(--ok);text-decoration:line-through}

  /* ── Route ── */
  .route-item{display:flex;align-items:center;gap:14px;padding:15px 18px;border-radius:var(--r);border:1px solid var(--border);margin-bottom:10px;background:var(--surface);box-shadow:var(--shadow-sm);transition:.2s var(--ease)}
  .route-item:hover{transform:translateY(-1px);box-shadow:var(--shadow)}
  .route-item.completed{opacity:.5}
  .route-num{width:32px;height:32px;border-radius:50%;background:var(--accent);color:#fff;font-weight:600;font-size:13px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .route-num.done{background:var(--ok)}
  .route-info{flex:1}
  .route-name{font-weight:600;font-size:13.5px;color:var(--text)}
  .route-addr{font-size:12px;color:var(--text2);margin-top:2px}

  /* ── Today banner (dark hero) ── */
  .today-banner{background:${NAVY};border-radius:var(--r-lg);padding:24px 26px;color:${WHITE};margin-bottom:22px;display:flex;align-items:center;justify-content:space-between;box-shadow:var(--shadow)}
  .today-banner h3{font-size:19px;font-weight:600;letter-spacing:-.02em}
  .today-banner p{font-size:13px;opacity:.62;margin-top:4px}
  .today-num{font-size:56px;font-weight:700;color:var(--accent);line-height:1;letter-spacing:-.04em}

  /* ── Tabs (underline, accent) ── */
  .tabs{display:flex;gap:4px;border-bottom:1px solid var(--border);margin-bottom:18px}
  .tab{padding:9px 16px;cursor:pointer;font-size:13.5px;font-weight:500;color:var(--text2);border-bottom:2px solid transparent;margin-bottom:-1px;transition:.16s var(--ease)}
  .tab:hover{color:var(--text)}
  .tab.active{color:var(--accent);border-bottom-color:var(--accent);font-weight:600}

  /* ── Price tag ── */
  .price-tag{background:#fafafd;border:1px solid var(--border);border-radius:var(--r);padding:14px 18px;font-size:26px;font-weight:600;color:var(--text);text-align:center;letter-spacing:-.03em}
  .price-tag span{font-size:13px;font-weight:400;color:var(--text2)}

  /* ── Grid ── */
  .grid-2{display:grid;grid-template-columns:1fr 1fr;gap:18px}
  .grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px}

  /* ── Empty state ── */
  .empty{text-align:center;padding:44px 20px;color:var(--text3)}
  .empty p{font-size:13.5px;margin-top:8px}

  /* ── Alerts ── */
  .alert{padding:12px 16px;border-radius:var(--r);font-size:13px;margin-bottom:14px}
  .alert-success{background:rgba(26,138,82,.1);color:var(--ok);border:1px solid rgba(26,138,82,.2)}
  .alert-error{background:rgba(215,50,43,.08);color:var(--danger);border:1px solid rgba(215,50,43,.2)}
  .alert-info{background:var(--accent-weak);color:var(--accent);border:1px solid rgba(0,113,227,.2)}

  /* ── Stripe field ── */
  .stripe-field{padding:10px 13px;border:1px solid var(--border);border-radius:10px;background:var(--surface);transition:.16s var(--ease);min-height:40px}
  .stripe-field.focused{border-color:var(--accent);box-shadow:0 0 0 4px var(--accent-weak)}
  .stripe-field.error{border-color:var(--danger)}

  /* ── Steps ── */
  .steps{display:flex;gap:0;margin-bottom:24px}
  .step{flex:1;display:flex;flex-direction:column;align-items:center;position:relative}
  .step:not(:last-child)::after{content:'';position:absolute;top:13px;left:50%;width:100%;height:2px;background:var(--border);z-index:0}
  .step-dot{width:28px;height:28px;border-radius:50%;border:2px solid var(--border);background:var(--surface);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;color:var(--text3);position:relative;z-index:1;transition:.2s var(--ease)}
  .step-dot.active{border-color:var(--accent);background:var(--accent);color:#fff}
  .step-dot.done{border-color:var(--ok);background:var(--ok);color:#fff}
  .step-label{font-size:11px;margin-top:6px;color:var(--text2);text-align:center}
  .step-label.active{color:var(--accent);font-weight:600}

  /* ── Card display ── */
  .card-display{background:${NAVY};border-radius:var(--r-lg);padding:16px 20px;color:${WHITE};display:flex;align-items:center;justify-content:space-between;box-shadow:var(--shadow)}

  /* ── Divider ── */
  .divider{height:1px;background:var(--border);margin:18px 0}

  /* ── Section header ── */
  .section-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px}
  .section-title{font-size:15px;font-weight:600;color:var(--text);letter-spacing:-.02em}

  /* ── Spinner ── */
  @keyframes spin{to{transform:rotate(360deg)}}
  .spinner{width:15px;height:15px;border:2px solid rgba(255,255,255,.35);border-top-color:${WHITE};border-radius:50%;animation:spin .7s linear infinite;display:inline-block}
  .spinner-dark{border-color:var(--accent-weak);border-top-color:var(--accent)}

  /* ── Profile ── */
  .profile-header{display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:14px;margin-bottom:18px}
  .profile-avatar{width:52px;height:52px;border-radius:50%;background:${NAVY};color:var(--accent);display:flex;align-items:center;justify-content:center;font-size:19px;font-weight:600;flex-shrink:0}

  /* ── Hide number input spinners ── */
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button { -webkit-appearance:none; margin:0; }
  input[type=number] { -moz-appearance:textfield; }

  /* ── Misc utilities ── */
  .flex{display:flex}.items-center{align-items:center}.justify-between{justify-content:space-between}.gap-2{gap:8px}.gap-3{gap:12px}
  .text-sm{font-size:12px}.text-gray{color:var(--text2)}.font-bold{font-weight:600}
  .mt-4{margin-top:16px}.mb-4{margin-bottom:16px}

  /* ── Sidebar nav badge ── */
  .nav-badge{margin-left:auto;background:var(--accent-weak);color:var(--accent);font-size:10px;font-weight:700;padding:1px 7px;border-radius:10px}
  .nav-badge.red{background:rgba(215,50,43,.22);color:#ff8a7e}
  /* when the row is active (solid accent), keep the badge readable */
  .nav-item.active .nav-badge{background:rgba(255,255,255,.25);color:#fff}

  /* ── Topbar avatar + bell ── */
  .topbar-icon{width:34px;height:34px;border-radius:10px;border:1px solid var(--border);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--text2);position:relative;transition:.15s var(--ease)}
  .topbar-icon:hover{background:#fbfbfd;color:var(--text)}
  .topbar-avatar{width:34px;height:34px;border-radius:50%;background:${NAVY};display:flex;align-items:center;justify-content:center;color:var(--accent);font-weight:600;font-size:12px;cursor:pointer}
  .notif-badge{position:absolute;top:-4px;right:-4px;width:17px;height:17px;border-radius:50%;background:var(--danger);color:#fff;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;border:2px solid var(--surface)}

  /* ── Activity feed ── */
  .activity-item{display:flex;gap:11px;padding:10px 0;border-bottom:1px solid var(--border)}
  .activity-item:last-child{border-bottom:none}
  .activity-dot{width:8px;height:8px;border-radius:50%;margin-top:5px;flex-shrink:0}
  .activity-text{font-size:12.5px;color:var(--text);line-height:1.45}
  .activity-time{font-size:11px;color:var(--text3);margin-top:2px}

  /* ── Revenue chart ── */
  .chart-bar-wrap{display:flex;align-items:flex-end;gap:7px;height:86px;padding-top:8px}
  .chart-bar-col{flex:1;display:flex;flex-direction:column;align-items:stretch}
  .chart-bar{flex:1;background:#f0f0f3;border-radius:5px 5px 0 0;position:relative;overflow:hidden}
  .chart-bar-fill{position:absolute;bottom:0;left:0;right:0;border-radius:5px 5px 0 0;transition:.5s var(--ease)}
  .chart-label{font-size:9.5px;color:var(--text3);text-align:center;margin-top:5px;font-weight:500}

  /* ── Quick actions ── */
  .quick-action{display:flex;flex-direction:column;align-items:center;gap:8px;padding:16px 8px;border-radius:var(--r);border:1px solid var(--border);background:var(--surface);cursor:pointer;transition:.18s var(--ease);text-align:center;box-shadow:var(--shadow-sm)}
  .quick-action:hover{transform:translateY(-2px);box-shadow:var(--shadow);border-color:rgba(0,113,227,.3)}
  .qa-icon{width:38px;height:38px;border-radius:11px;background:#f5f5f7;display:flex;align-items:center;justify-content:center;font-size:17px}
  .quick-action span{font-size:11.5px;font-weight:500;color:var(--text2)}

  /* ── Stat trend ── */
  .stat-trend{font-size:11.5px;font-weight:500;margin-top:6px}
  .trend-up{color:var(--ok)}.trend-down{color:var(--danger)}

  /* ── Weather ── */
  .weather-widget{background:${NAVY};border-radius:var(--r-lg);padding:18px;color:${WHITE};box-shadow:var(--shadow)}

  /* ── Progress ── */
  .progress-bar{height:6px;background:#ececf0;border-radius:3px;overflow:hidden}
  .progress-fill{height:100%;border-radius:3px;transition:.5s var(--ease)}

  /* ── Map ── */
  .map-container{background:#e8e8ed;border-radius:var(--r-lg);overflow:hidden;position:relative;border:1px solid var(--border);box-shadow:var(--shadow-sm)}
  .map-marker{position:absolute;transform:translate(-50%,-50%);cursor:pointer;transition:.18s var(--ease);z-index:2}
  .map-marker:hover{z-index:10;transform:translate(-50%,-50%) scale(1.3)}
  .map-tooltip{position:absolute;background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:9px 11px;font-size:11px;white-space:nowrap;box-shadow:var(--shadow);z-index:20;bottom:calc(100%+8px);left:50%;transform:translateX(-50%);pointer-events:none;min-width:140px}
  .map-tooltip::after{content:'';position:absolute;top:100%;left:50%;transform:translateX(-50%);border:5px solid transparent;border-top-color:var(--surface)}
  .map-legend{position:absolute;bottom:14px;left:14px;background:rgba(255,255,255,.82);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);border-radius:12px;padding:11px 13px;font-size:11px;border:1px solid var(--border);box-shadow:var(--shadow)}
  .legend-row{display:flex;align-items:center;gap:7px;margin-bottom:5px;color:var(--text2);font-weight:500;font-size:11px}
  .legend-row:last-child{margin-bottom:0}
  .map-controls{position:absolute;top:14px;right:14px;display:flex;flex-direction:column;gap:5px;z-index:5}
  .map-ctrl-btn{width:30px;height:30px;background:rgba(255,255,255,.9);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);border:1px solid var(--border);border-radius:9px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:14px;color:var(--text2);font-weight:700;transition:.15s var(--ease)}
  .map-ctrl-btn:hover{background:#fff;color:var(--text)}
  .map-block{position:absolute;background:#d4d4da;border-radius:2px}
  .map-road-h{position:absolute;height:1px;background:#fff;left:0;right:0;opacity:.35}
  .map-road-v{position:absolute;width:1px;background:#fff;top:0;bottom:0;opacity:.35}

  /* ── Apple-style scannable list rows (used by redesigned list screens) ── */
  .alist-row{display:flex;align-items:center;gap:14px;padding:14px 8px;border-bottom:1px solid var(--border);cursor:pointer;transition:background .14s var(--ease);border-radius:10px}
  .alist-row:hover{background:#fafafd}
  .alist-row:last-child{border-bottom:none}
  .amono{width:38px;height:38px;border-radius:50%;background:var(--accent-weak);color:var(--accent);display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;flex-shrink:0;letter-spacing:.02em}
  /* Segmented view-switcher (Schedule tabs, Clients tabs). Elevated onto the
     card surface with a hairline + soft shadow so it carries the same depth as
     the rest of the page instead of reading as a faint gray afterthought. The
     active segment fills with Apple blue — the same accent as the hero, the
     sidebar's active item and primary CTAs — so it stands out and ties in. */
  .seg{display:inline-flex;align-self:flex-start;width:fit-content;background:var(--surface);border:1px solid var(--border);border-radius:13px;padding:4px;gap:3px;box-shadow:var(--shadow-sm)}
  .seg button{border:none;background:transparent;padding:9px 18px;border-radius:9px;font:inherit;font-size:13.5px;font-weight:500;color:var(--text2);cursor:pointer;display:flex;align-items:center;gap:7px;transition:.18s var(--ease);letter-spacing:-.01em}
  .seg button:hover:not(.on){color:var(--text);background:var(--accent-weak)}
  .seg button.on{background:var(--accent);color:#fff;font-weight:600;box-shadow:0 1px 2px rgba(0,113,227,.3),0 6px 16px -5px rgba(0,113,227,.5)}

  /* ── Settings content pane — DE-BOXED: one continuous surface with sections
        divided by hairlines (macOS System Settings), not floating card boxes. ── */
  .settings-pane{background:var(--surface);border:1px solid var(--border);border-radius:22px;box-shadow:var(--shadow);padding:10px 44px;max-width:760px}
  .settings-pane .grid-2{grid-template-columns:1fr;gap:0;margin:0 !important}
  .settings-pane .card{background:transparent !important;border:none !important;box-shadow:none !important;border-radius:0 !important;margin:0 !important;padding:42px 0 !important;border-top:1px solid var(--border) !important}
  .settings-pane .card:hover{transform:none;box-shadow:none}
  .settings-pane > :first-child .card:first-child,.settings-pane > .card:first-child{border-top:none !important}
  /* pronounced-but-minimal section headers */
  .settings-pane .card-title{font-size:17px !important;font-weight:600 !important;letter-spacing:-.015em;color:var(--text) !important;margin-bottom:18px !important}

  /* ── .calm-pane — the reusable de-boxed surface (carry the Settings feel to
        any screen: one panel, hairline-divided sections, no floating boxes).
        It is a no-op in the default theme, so wrapping with it stays reversible. ── */
  .calm-pane{background:var(--surface);border:1px solid var(--border);border-radius:20px;box-shadow:var(--shadow);padding:6px 32px}
  .calm-pane .grid-2,.calm-pane .grid-3{grid-template-columns:1fr !important;gap:0 !important;margin:0 !important}
  .calm-pane .card{background:transparent !important;border:none !important;box-shadow:none !important;border-radius:0 !important;margin:0 !important;padding:34px 0 !important;border-top:1px solid var(--border) !important}
  .calm-pane .card:hover{transform:none;box-shadow:none}
  .calm-pane > :first-child .card:first-child,.calm-pane > .card:first-child{border-top:none !important}
  .calm-pane .card-title{font-size:17px !important;font-weight:600 !important;letter-spacing:-.015em;color:var(--text) !important;margin-bottom:18px !important}

  /* section start-markers — a small accent eyebrow so section boundaries register
     without reintroducing boxes; the surface stays flat and calm. */
  .settings-pane .card-title,.calm-pane .card-title{position:relative;padding-left:15px}
  .settings-pane .card-title::before,.calm-pane .card-title::before{content:"";position:absolute;left:0;top:2px;bottom:2px;width:3px;border-radius:2px;background:var(--accent)}
  /* and make the dividing line itself a touch more present */
  .settings-pane .card,.calm-pane .card{border-top-color:rgba(0,0,0,.11) !important}

  /* wide tables: hold action buttons quiet until the row is hovered/focused */
  table .row-actions{opacity:0;transition:opacity .15s var(--ease)}
  tr:hover .row-actions,.row-actions:focus-within{opacity:1}

  /* ── Mobile (≤768px): off-canvas sidebar drawer + fluid layout. Desktop (>768px) is untouched. ── */
  .sidebar-toggle{display:none;align-items:center;justify-content:center;width:38px;height:38px;border:1px solid var(--border);border-radius:var(--r-sm);background:var(--surface);color:var(--text);font-size:18px;line-height:1;cursor:pointer;flex-shrink:0;padding:0}
  .sidebar-backdrop{display:none;position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:110}
  @media (max-width:768px){
    /* Hard stop: nothing scrolls sideways on a phone. overflow-x:clip is the
       iOS-Safari-robust one (Safari honors 'hidden' inconsistently on nested
       scroll containers, which let the whole page pan sideways); the preceding
       'hidden' is the fallback for pre-16 Safari. */
    html,body,#root{overflow-x:hidden;overflow-x:clip;max-width:100%}
    .app{display:block;height:auto;overflow:visible}
    .content{max-width:100%}
    .content img,.content video{max-width:100%;height:auto}
    /* any inline flex row that spaces items apart wraps instead of overflowing */
    .card > div[style*="space-between"]{flex-wrap:wrap}
    .sidebar{position:fixed;top:0;left:0;bottom:0;height:100vh;height:100dvh;width:84%;max-width:300px;z-index:120;transform:translateX(-100%);transition:transform .25s var(--ease);box-shadow:0 0 44px rgba(0,0,0,.4)}
    .sidebar.open{transform:translateX(0)}
    .sidebar-backdrop.open{display:block}
    /* On a phone the document itself scrolls — NOT .main. As a nested
       overflow-y:auto container, .main traps scroll on iOS (feels stuck) and
       lets it pan sideways. overflow-y:visible + overflow-x:clip makes .main a
       plain block: vertical content flows into the normal page scroll (always
       works on iOS), horizontal overflow is clipped, no inner scroller. */
    .main{min-height:100vh;overflow-x:hidden;overflow-x:clip;overflow-y:visible}
    .topbar{position:sticky;top:0;z-index:60;padding:12px 12px;gap:8px}
    .topbar h2{font-size:16px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0}
    .sidebar-toggle{display:inline-flex}
    .content{padding:16px 13px}
    .grid-2,.grid-3{grid-template-columns:1fr;gap:14px}
    .today-banner{flex-direction:column;align-items:flex-start;gap:12px;padding:18px}
    .modal-overlay{padding:0;align-items:flex-end}
    .modal{max-width:100%;max-height:92vh;border-radius:var(--r-xl) var(--r-xl) 0 0}
    .topbar-date,.topbar-note{display:none}
    .topbar-actions{flex-shrink:0}
    /* iOS AUTO-ZOOM GUARD. Any input/select/textarea under 16px makes Safari zoom the
       page on focus and it NEVER zooms back — the whole session stays clipped on the
       right (reproduced in the iOS Simulator on the Leads search box, 2026-09-06).
       !important is deliberate: it beats the inline fontSize:12/13 sprinkled through
       the forms (AuthPage, ClientProfile, Charge/Refund modals…) in one place. */
    input:not([type=checkbox]):not([type=radio]):not([type=range]),select,textarea{font-size:16px !important}
    .stripe-field,.StripeElement{font-size:16px}
    /* Touch has no hover: table row actions must stay visible. */
    table .row-actions{opacity:1}
    /* iOS type=date ignores width:100% unless the native appearance is dropped. */
    input[type=date],input[type=time],input[type=datetime-local]{-webkit-appearance:none;appearance:none;min-width:0;min-height:44px}
    /* Comfortable tap targets for the standard button sizes. */
    .btn{min-height:40px;padding-left:13px;padding-right:13px}
    .btn-sm{min-height:36px;padding:8px 12px;font-size:12.5px}
    .btn-xs{min-height:34px;padding:7px 11px;font-size:12px}
    /* Button groups wrap instead of vanishing under overflow-x:clip. */
    .card div[style*="display: flex"],.card div[style*="display:flex"],.modal-footer,.section-header{flex-wrap:wrap}
    /* Tab strips (ClientProfile 5 tabs, Reports 6) scroll sideways instead of clipping the last ones. */
    .tabs{overflow-x:auto;flex-wrap:nowrap;-webkit-overflow-scrolling:touch;scrollbar-width:none;margin-left:-13px;margin-right:-13px;padding:0 13px}
    .tabs::-webkit-scrollbar{display:none}
    .tab{white-space:nowrap;flex-shrink:0;padding:10px 12px}
    /* Modals are bottom sheets: never taller than the VISIBLE viewport (100vh is the large
       viewport on iOS, so 92vh put the Save button under Safari's toolbar), own scroll,
       no scroll-chaining into the page, safe-area padding, 40px close target. */
    .modal-overlay{overscroll-behavior:contain}
    .modal{max-height:85vh;max-height:85dvh;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;padding-bottom:env(safe-area-inset-bottom)}
    .modal-header{padding:16px 18px}
    .modal-body{padding:16px 18px}
    .modal-footer{padding:12px 18px calc(12px + env(safe-area-inset-bottom))}
    .close-btn{width:40px;height:40px;padding:0;display:inline-flex;align-items:center;justify-content:center;border-radius:10px}
    body.sheet-open{overflow:hidden;position:fixed;left:0;right:0}
    /* Stat rows: 4 columns → 2; numbers shrink to fit. */
    .stats-row{grid-template-columns:1fr 1fr;gap:10px}
    .stat-card{padding:14px 16px}
    .stat-value{font-size:24px}
    /* Any table not already inside .table-wrap becomes its own sideways scroller. */
    .card > table,.modal-body table,.calm-pane table,.settings-pane table{display:block;overflow-x:auto;max-width:100%;-webkit-overflow-scrolling:touch}
    th,td{padding:10px 10px}
    /* De-boxed panes keep desktop's 32-44px gutters otherwise (≈261px of content left). */
    .settings-pane{padding:6px 14px;border-radius:16px}
    .settings-pane .card{padding:24px 0 !important}
    .calm-pane{padding:4px 12px;border-radius:16px}
    .calm-pane .card{padding:20px 0 !important}
    .form-grid{grid-template-columns:1fr;gap:12px}
    /* Drawer rows are finger-sized. */
    .nav-item{padding:13px 12px;font-size:15px}
    .sidebar .signout-btn{padding:12px 12px !important;font-size:14px !important;min-height:44px}
    /* App-level overlays (see App.js): toast + notifications ride under the topbar, full width. */
    .toast-wrap{position:fixed;top:68px;left:12px;right:12px;z-index:200;padding:0 !important;pointer-events:none}
    .toast-wrap .alert{box-shadow:var(--shadow-lg);pointer-events:auto}
    .notif-dd{position:fixed !important;left:12px !important;right:12px !important;top:68px !important;width:auto !important}
  }
  /* Any touch-only device (also tablets above 768px): hover-revealed row actions stay visible. */
  @media (hover:none){ table .row-actions{opacity:1} }
`;
